/**
 * 계약자 정보 수정 컴포넌트
 * 계약의 계약자 정보(이메일, 회원번호, 휴대폰)를 수정하는 컴포넌트입니다.
 */

import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { ContractManagaDetail, Contractor, DetailTotalData } from "src/api/contract/contract-types";
import { BaseButton } from "src/components";
import { formatPhoneNumber } from "src/utils/common-util";
import BaseSection from "../../../../components/layout/BaseSection";
import { default as useContactApi } from "../../hooks/useContractApi";
import ContractorChangeModal from "../modal/ContractorChangeModal";

/**
 * 계약자 정보 수정 컴포넌트의 props 인터페이스
 */
interface ContractBasicProp {
  detailData: DetailTotalData; // 상품 상세 조회 정보
  setData: Function; // 데이터 업데이트 함수
  setEditable: any; // 편집 가능 상태 설정 함수
  saveFlags: boolean; // 저장 플래그
  setSaveFlags: any; // 저장 플래그 설정 함수
  popupState: boolean; // 팝업 상태
  setPopupState: any; // 팝업 상태 설정 함수
  setCommonModals: any; // 공통 모달 설정 함수
  setEditConfrim: any; // 편집 확인 설정 함수
}

/**
 * 계약자 정보 수정 컴포넌트
 * @param detailData 상품 상세 조회 정보
 * @param setData 데이터 업데이트 함수
 * @param setEditable 편집 가능 상태 설정 함수
 * @param saveFlags 저장 플래그
 * @param setSaveFlags 저장 플래그 설정 함수
 * @param setCommonModals 공통 모달 설정 함수
 * @param setPopupState 팝업 상태 설정 함수
 * @param setEditConfrim 편집 확인 설정 함수
 */
const ContractorNOrganizationEdit = ({
  detailData,
  setData,
  setEditable,
  saveFlags,
  setSaveFlags,
  setCommonModals,
  setPopupState,
  setEditConfrim,
}: ContractBasicProp) => {
  const { editContractManageData } = useContactApi();
  const { contract, contractManage, product } = detailData;
  // 공간임대(FULL_COURT)는 해지완료/이용완료 상태에서도 계약자 변경 가능 (비상주/공간예약은 현행 유지)
  const isFullCourt = product?.productType === "FULL_COURT";

  // 상태 관리
  const [contractor, setContractor] = useState<Contractor>(); // 계약자 정보
  const [editContractor, setEditContractor] = useState<Contractor>(); // 수정 중인 계약자 정보
  const [isChangeContractor, setIsChangeContractor] = useState(false); // 계약자 변경 모달 표시 여부
  const [editContractManage, setEditContractManage] = useState<ContractManagaDetail>(); // 수정 중인 계약 관리 정보

  /**
   * 계약자 변경 처리
   * @param member 변경할 회원 정보
   */
  const onChangeContractor = useCallback(
    async (member: any) => {
      if (member) {
        setIsChangeContractor(false);
        const temp = _.cloneDeep(contractManage!);

        //화면에 보여주기 위한 데이터 작업
        const changeUser: Contractor = {
          isContractor: true,
          isAdmin: true,
          isDeleted: false,
          memberEmail: member.email,
          memberPhoneNumber: member.inviteMobileNumber ? member.inviteMobileNumber : member.phoneNumber,
          memberNo: member.memberNo,
        };

        //전송할 멤버 정보를 위한 데이터 작업
        member.cmdType = "U";
        member.isContractor = true;
        member.isAdmin = true;
        member.isDeleted = false;
        member.contractMemberId = 0;
        member.inviteMobileNumber = member.phoneNumber;
        delete member.phoneNumber;

        temp.contractorList = [member];

        setEditContractor(changeUser);
        setEditContractManage(temp);
      }
    },
    [contractManage],
  );

  /**
   * 데이터 초기화
   * 계약자 정보를 초기화합니다.
   */
  const initDatas = useCallback(() => {
    if (contractManage && contractManage?.contractorList) {
      setEditContractManage(contractManage!);
      const findedContractor = contractManage?.contractorList!.find((user: Contractor) => user.isContractor);
      if (findedContractor) {
        setContractor(_.cloneDeep(findedContractor));
        setEditContractor(findedContractor);
      }
    }
  }, [contractManage]);

  // 컴포넌트 마운트 시 데이터 초기화
  useEffect(() => {
    initDatas();
  }, [initDatas]);

  /**
   * 계약 관리 데이터 저장
   * 수정된 계약자 정보를 저장하고 결과에 따라 적절한 메시지를 표시합니다.
   */
  const onSaveContractManageData = useCallback(async () => {
    try {
      const result: any = await editContractManageData(contractManage!, editContractManage!);
      // 성공 (2xx)
      if (result.response.status! > 199 && result.response.status! < 300) {
        setCommonModals({
          message: "변경되었습니다.",
          title: "계약자 정보 변경",
        });
        setPopupState(true);
        setEditable(false);
        setData();
        return;
      }
      // 검증 실패 (변경사항 없음 등)
      if (result.validationPass === false) {
        setCommonModals({
          message: result.message,
          title: "계약자 정보 변경",
        });
        setPopupState(true);
        return;
      }
      // 그 외 실패(서버 오류 등)는 공통 API 실패 모달이 처리하므로 별도 모달 없이 finally에서 플래그만 리셋
    } catch (error) {
      // 공통 API 실패 모달이 처리하므로 별도 모달은 띄우지 않음
      console.error("계약자 정보 저장 실패:", error);
    } finally {
      // 결과와 무관하게 플래그를 반드시 리셋해, 다음 저장 시도가 정상 트리거되도록 함
      setEditConfrim(false);
      setSaveFlags(false);
    }
  }, [
    editContractManageData,
    contractManage,
    editContractManage,
    setCommonModals,
    setPopupState,
    setSaveFlags,
    setEditable,
    setData,
    setEditConfrim,
  ]);

  //저장 플래그 변경 시에만 반응 하도록
  useEffect(() => {
    // 저장 클릭 시
    if (saveFlags === true) {
      onSaveContractManageData();
    } else {
      //취소 클릭 시
      initDatas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveFlags, setSaveFlags]);

  return (
    <>
      <div className="flex-center">
        <BaseSection
          title={"이메일"}
          children={
            <div className="flex-row flex-center-start">
              <div className="minmax200 mr8">
                <p>{editContractor?.memberEmail}</p>
              </div>

              <div className="minmax90">
                <BaseButton
                  disabled={
                    contract?.contractStep === "APPLY_RECEIVED" ||
                    contract?.contractStep === "APPLY_CANCEL" ||
                    (!isFullCourt &&
                      (contract?.contractStep === "USE_COMPLETE" || contract?.contractStep === "TERMINATE_COMPLETE"))
                  }
                  title="변경"
                  onClick={() => {
                    setIsChangeContractor(true);
                  }}
                  className="color-white"
                />
                {isChangeContractor && (
                  <ContractorChangeModal
                    isOpen={isChangeContractor}
                    memberInfo={contractManage}
                    onClick={onChangeContractor}
                    onClose={() => setIsChangeContractor(false)}
                  />
                )}
              </div>
            </div>
          }
        />
        <BaseSection title={"회원 번호"} value={editContractor?.memberNo} />
      </div>
      <div className="flex-center">
        <BaseSection title={"휴대폰"} value={formatPhoneNumber(String(editContractor?.memberPhoneNumber))} />
      </div>
    </>
  );
};

export default ContractorNOrganizationEdit;
